import {
  FileOutlined,
  FolderOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusCircleOutlined,
  ReadOutlined,
  SettingOutlined,
  ShopOutlined,
  TagOutlined,
  TagsOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Breadcrumb } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidPackage } from 'react-icons/bi';
import { useLocation } from 'react-router';
import { useProfile } from '../../../utils/zustand/hooks/useProfile';
import { StyledBreadcrumbLabel, StyledBreadcrumbLink } from './styled';

const routeConfig = ({ profile, path, t }) => {
  const route = {
    dashboard: {
      icon: <HomeOutlined />,
      label: t('dashboard:navigation.dashboard'),
    },
    users: {
      icon: <UserOutlined />,
      label: t('dashboard:navigation.users'),
    },
    settings: {
      icon: <SettingOutlined />,
      label: t('dashboard:navigation.settings'),
    },
    marketplaces: {
      icon: <ShopOutlined />,
      label: t('dashboard:navigation.marketplaces'),
    },
    products: {
      icon: <ReadOutlined />,
      label: t('dashboard:navigation.products'),
    },
    'mp-products': {
      icon: <ReadOutlined />,
      label: t('dashboard:navigation.mp-products'),
    },
    categories: {
      icon: <TagOutlined />,
      label: t('dashboard:navigation.categories'),
    },
    medias: {
      icon: <PictureOutlined />,
      label: t('dashboard:navigation.medias'),
    },
    attributes: {
      icon: <SettingOutlined />,
      label: t('dashboard:navigation.attributes'),
    },
    associations: {
      icon: <LinkOutlined />,
      label: t('dashboard:navigation.associations'),
    },
    'mass-processing': {
      icon: <ToolOutlined />,
      label: t('dashboard:navigation.mass_processing'),
    },
    profile: {
      icon: <UserOutlined />,
      label: `${profile?.given_name} ${profile?.family_name}`,
    },
    'structured-data': {
      icon: <FileOutlined />,
      label: t('dashboard:navigation.structured-data'),
    },
    'category-details': {
      icon: <InfoCircleOutlined />,
      label: t('dashboard:navigation.category-details'),
    },
    'mp-category-attributes': {
      icon: <TagsOutlined />,
      label: t('dashboard:navigation.category-attributes'),
    },
    'mp-category-products': {
      icon: <BiSolidPackage />,
      label: t('dashboard:navigation.category-products'),
    },
    'add-categories': {
      icon: <PlusCircleOutlined />,
      label: t('dashboard:navigation.add-categories'),
    },
    'add-mp-attributes': {
      icon: <PlusCircleOutlined />,
      label: t('dashboard:navigation.add-mp-attributes'),
    },
    mapping: {
      icon: <LinkOutlined />,
      label: t('dashboard:navigation.mapping'),
    },
    'media-library': {
      icon: <PictureOutlined />,
      label: t('dashboard:navigation.media-library'),
    },
    'media-qualification': {
      icon: <PictureOutlined />,
      label: t('dashboard:navigation.media-qualification'),
    },
    default: {
      icon: <FolderOutlined />,
      label: null,
    },
  };

  return route[path] || route.default;
};

const getRouteInfo = ({ profile, path, t }) => {
  const route = routeConfig({ profile, path, t });
  return {
    icon: route.icon,
    label: route.label || path.charAt(0).toUpperCase() + path.slice(1),
  };
};

const DashboardBreadcrumbs = () => {
  const location = useLocation();
  const { t } = useTranslation(['dashboard', 'common']);

  const { profile } = useProfile();

  // Fonction pour construire les items du breadcrumb
  const getBreadcrumbItems = () => {
    const pathSnippets = location.pathname.split('/').filter((i) => i);

    // On commence toujours par le dashboard
    const items = [
      {
        title: (
          <StyledBreadcrumbLink to="/dashboard">
            {getRouteInfo({ profile, path: 'dashboard', t }).icon}
            <StyledBreadcrumbLabel>
              {getRouteInfo({ profile, path: 'dashboard', t }).label}
            </StyledBreadcrumbLabel>
          </StyledBreadcrumbLink>
        ),
        key: 'dashboard',
      },
    ];

    // On construit le chemin progressivement
    let currentPath = '';
    pathSnippets.forEach((snippet) => {
      if (snippet !== 'dashboard' && snippet !== 'home') {
        currentPath += `/${snippet}`;
        const { icon, label } = getRouteInfo({ profile, path: snippet, t });
        items.push({
          title: (
            <StyledBreadcrumbLink to={`/dashboard${currentPath}`}>
              {icon}
              <StyledBreadcrumbLabel>{label}</StyledBreadcrumbLabel>
            </StyledBreadcrumbLink>
          ),
          key: snippet,
        });
      }
    });

    return items;
  };

  return (
    <Breadcrumb style={{ margin: '8px 0' }} items={getBreadcrumbItems()} />
  );
};

export default DashboardBreadcrumbs;
